import { create } from "zustand";
import { deepMerge } from "utils/mergeObjects";
import { SceneExtended } from "./types";

/**
 * Only the fields being changed. Arrays are replaced wholesale rather than
 * merged element-wise — same rule deepMerge follows.
 */
export type ScenePatch<T = SceneExtended> = T extends (infer _U)[]
	? T
	: T extends object
	? { [K in keyof T]?: ScenePatch<T[K]> }
	: T;

interface SceneDocState {
	/**
	 * The currently open scene, and the single source of truth for it.
	 *
	 * This lives in a store rather than in ScenesProvider's React state so that
	 * save callbacks can read the latest document synchronously via
	 * `useSceneDocStore.getState().doc`, instead of whatever their closure
	 * captured at render time. That distinction matters: every save PUTs field
	 * values read from here, so a stale read doesn't just show the wrong thing —
	 * it writes the wrong thing back to the database.
	 */
	doc: SceneExtended | undefined;
	/** Replace the document. Accepts a value or an updater, like a setState. */
	setDoc: React.Dispatch<React.SetStateAction<SceneExtended | undefined>>;
	/**
	 * Deep-merge a patch onto the document. Always prefer this over reading the
	 * document, spreading it and calling setDoc: the merge happens against the
	 * live state, so a caller holding an outdated copy can only ever change the
	 * fields it names, never revert the ones it doesn't.
	 */
	patchDoc: (patch: ScenePatch) => void;
}

export const useSceneDocStore = create<SceneDocState>((set) => ({
	doc: undefined,
	setDoc: (next) =>
		set((state) => ({
			doc:
				typeof next === "function"
					? (next as (
							prev: SceneExtended | undefined,
					  ) => SceneExtended | undefined)(state.doc)
					: next,
		})),
	patchDoc: (patch) =>
		set((state) => ({
			doc: state.doc
				? ((deepMerge(
						(state.doc as unknown) as Record<string, unknown>,
						(patch as unknown) as Record<string, unknown>,
				  ) as unknown) as SceneExtended)
				: state.doc,
		})),
}));
